/**
 * Portfolio Item Component
 * 
 * Displays an individual portfolio item in the gallery.
 * Shows only the image with border-radius and dynamic grid positioning.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.imageSrc - Source path to the portfolio image
 * @param {string} props.imageAlt - Alternative text for the image (accessibility)
 * @returns {JSX.Element} Individual portfolio image
 */

import React, { useRef, useEffect } from 'react';
import './portfolio-item-style.css';

const PortfolioItem = ({ imageSrc, imageAlt }) => {
  const imageRef = useRef(null);

  const setGridSpan = () => {
    const img = imageRef.current;
    if (!img || !img.clientHeight) return;
    const rowHeight = 1; // matches grid-auto-rows: 1px
    const gap = 8;       // matches CSS row-gap: 8px
    const span = Math.ceil((img.clientHeight + gap) / (rowHeight + gap));
    img.style.gridRowEnd = `span ${span}`;
  };

  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;

    if (img.complete && img.naturalHeight > 0) {
      setGridSpan();
    } else {
      img.addEventListener('load', setGridSpan);
    }

    const resizeObserver = new ResizeObserver(setGridSpan);
    resizeObserver.observe(img);

    return () => {
      img.removeEventListener('load', setGridSpan);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <img 
      ref={imageRef}
      src={imageSrc} 
      alt={imageAlt}
      className="portfolio-item"
      loading="lazy"
    />
  );
};

export default PortfolioItem;
